/**
 * WaveformMini - Simplified waveform visualization for the audio list
 * Shows waveform bars with playhead position during playback
 */

import { useRef, useEffect, useState } from "react";
import { useWaveformPeaksLoader } from "@/hooks/useWaveformPeaksLoader";
import "./WaveformMini.css";

interface WaveformMiniProps {
  assetId: string;
  projectId: string;
  progress: number; // 0-100
  currentTime: number;
  duration: number;
  onSeek: (time: number) => void;
  /** Hide the current time display to give waveform more space */
  hideCurrentTime?: boolean;
  /** Hide the duration display (when shown separately) */
  hideDuration?: boolean;
}

// Format seconds as mm:ss
function formatTime(seconds: number): string {
  if (!seconds || !isFinite(seconds)) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

export function WaveformMini({
  assetId,
  projectId,
  progress,
  currentTime,
  duration,
  onSeek,
  hideCurrentTime = false,
  hideDuration = false,
}: WaveformMiniProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isHovering, setIsHovering] = useState(false);
  const [hoverPosition, setHoverPosition] = useState(0);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });

  // Load waveform peaks data
  const { data: waveformData, isLoading } = useWaveformPeaksLoader(
    assetId,
    projectId,
  );

  // Track container size with ResizeObserver (container has fixed height)
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateSize = () => {
      const rect = container.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        setCanvasSize({ width: rect.width, height: rect.height });
      }
    };

    // Initial size check
    updateSize();

    const observer = new ResizeObserver(() => {
      updateSize();
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // Draw waveform on canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;

    // Use state dimensions, or fall back to getBoundingClientRect
    let { width, height } = canvasSize;
    if (width === 0 || height === 0) {
      const rect = container.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
    }

    // Skip if still no valid dimensions
    if (width === 0 || height === 0) return;

    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx.scale(dpr, dpr);

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // If no data, draw placeholder bars
    // WaveformJson has peaks, peaksLeft, or peaksRight
    const peaks = waveformData?.peaks || waveformData?.peaksLeft || [];
    // Dynamic bar count based on width - one bar every ~5px for tight appearance
    const barWidth = 2;
    const minGap = 3;
    const barCount = Math.max(20, Math.floor(width / (barWidth + minGap)));
    const gap = (width - barCount * barWidth) / (barCount - 1);
    const centerY = height / 2;
    const maxHeight = height * 0.8;

    for (let i = 0; i < barCount; i++) {
      const x = i * (barWidth + gap);
      const progressPercent = (i / barCount) * 100;
      const isPlayed = progressPercent <= progress;

      // Get peak value or generate placeholder
      let peakValue: number;
      if (peaks.length > 0) {
        const peakIndex = Math.floor((i / barCount) * peaks.length);
        peakValue = Math.abs(peaks[peakIndex] || 0);
      } else {
        // Placeholder: deterministic pattern (no random)
        peakValue = 0.3 + Math.sin(i * 0.4) * 0.25 + Math.cos(i * 0.7) * 0.15;
      }

      const barHeight = Math.max(4, peakValue * maxHeight);

      // Color based on played/unplayed
      if (isPlayed) {
        ctx.fillStyle = "#2978c4"; // Bible Blue - played portion
      } else {
        ctx.fillStyle = "rgba(255, 255, 255, 0.25)"; // Unplayed
      }

      // Draw bar centered vertically
      ctx.fillRect(x, centerY - barHeight / 2, barWidth, barHeight);
    }

    // Draw hover indicator
    if (isHovering && duration > 0) {
      ctx.fillStyle = "rgba(255, 255, 255, 0.5)";
      ctx.fillRect(hoverPosition, 0, 1, height);
    }
  }, [waveformData, progress, isHovering, hoverPosition, duration, canvasSize]);

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!containerRef.current || duration <= 0) return;

    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const seekTime = percentage * duration;

    onSeek(Math.max(0, Math.min(duration, seekTime)));
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    setHoverPosition(e.clientX - rect.left);
  };

  return (
    <div className="waveform-mini-container">
      {!hideCurrentTime && (
        <span className="waveform-mini__time waveform-mini__time--current">
          {formatTime(currentTime)}
        </span>
      )}
      <div
        ref={containerRef}
        className="waveform-mini"
        onClick={handleClick}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        onMouseMove={handleMouseMove}
      >
        <canvas ref={canvasRef} className="waveform-mini__canvas" />
        {isLoading && <div className="waveform-mini__loading" />}
      </div>
      {!hideDuration && (
        <span className="waveform-mini__time waveform-mini__time--duration">
          {formatTime(duration)}
        </span>
      )}
    </div>
  );
}
